import { Component, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { DynamicDialogRef } from 'primeng/dynamicdialog';
import { CVLocalizationService } from 'src/app/reusables/services/cv-localization.service';
import { LoadFromFileObj, FieldTypes, SelectedFile, FileValidation, KeyValueObj } from './cxone-etl-load-from-file.model';
import moment from 'moment';

@Component({
    selector: 'cv-cxone-etl-load-from-file',
    templateUrl: './cxone-etl-load-from-file.component.html',
    styleUrls: ['./cxone-etl-load-from-file.component.scss']
})

export class CxoneEtlLoadFromFileComponent implements OnInit {
    public loadFromFileForm = new FormGroup({
        file: new FormControl("", [Validators.required]),
        uploadActions: new FormControl({ value: 'replace', disabled: false }, [Validators.required])
    });

    public selectedFileName: SelectedFile[];
    public headerTitle: string;
    public loading = false;
    public errorMessage: string;
    public warningMessage: string;
    public jsonFileData: LoadFromFileObj;
    public infoMessage: string;
    public actionFlag: string;
    public newFeedColumnData: KeyValueObj[] = [];

    public title: string;
    public cancelLabel: string;
    public saveLabel: string;
    public fileValidation: FileValidation;
    public replaceRadioLabel: string;
    public addMissingRadioLabel: string;
    public isLocalizationSectionLoaded: boolean;

    constructor(
        public dialogRef: DynamicDialogRef,
        private cvLocalizationService: CVLocalizationService)
    { }

    async ngOnInit(): Promise<void> {
        await this.loadLocalization();
        this.isLocalizationSectionLoaded = true;
    }

    async loadLocalization() {
        try {
            await this.cvLocalizationService.GetSection('ETL_Load_From_File');
        }
        catch {
            console.log('Failed to load localization for ETL_Load_From_File');
        }
        finally {
            this.title = this.getLocalizationString("Upload_File", "Upload File");
            this.saveLabel = this.getLocalizationString("Submit_Button", "Submit");
            this.cancelLabel = this.getLocalizationString("Cancel", "Cancel");
            this.infoMessage = this.getLocalizationString("Supported_File_Types", "Only JSON file (.txt or .json) is supported");
            this.headerTitle = this.getLocalizationString("Upload_Data", "Upload Data");
            this.replaceRadioLabel = this.getLocalizationString("Replace_Existing_Fields", "Replace Existing Fields");
            this.addMissingRadioLabel = this.getLocalizationString("Add_Missing_Fields", "Add Missing Fields");
            this.errorMessage = this.getLocalizationString("File_Type_Message", "Selected file is not supported");
            this.warningMessage =
                this.getLocalizationString("File_Warning_Message", "The expected values are not present in the selected file.");
        }
    }

    public getLocalizationString(key: string, defaultValue: string): string {
        const baseString = this.cvLocalizationService.getCachedText("ETL_Load_From_File", key, defaultValue);
        return baseString;
    }

    cancel() {
        this.dialogRef.close();
    }

    async validateAndLoadFile(event): Promise<void> {
        const files: File[] = event.target.files;
        this.loading = true;
        if (files && files.length > 0) {
            this.fileValidation = {};
            const uploadedFile = files[0];
            // Set the cx-one-tag array for displaying the file name in pill component.
            this.selectedFileName = [{
                id: 'file0',
                text: uploadedFile.name
            }];
            if (this.isFileTypeAllowed(uploadedFile)) {
                try {
                    this.fileValidation = await this.readAndValidateFile(uploadedFile);
                    if (this.fileValidation.isValid) {
                        this.prepareRowData();
                    }
                } catch (err) {
                    console.log('Error while reading the file ' + err.message);
                    this.fileValidation = {
                        isValid: false,
                        errorMessage: this.errorMessage
                    };
                }
            } else {
                // error message for file type incorrect
                this.fileValidation = {
                    isValid: false,
                    errorMessage: this.errorMessage
                };
            }
        }
        this.loading = false;
    }

    isFileTypeAllowed(file: File): boolean {
        // Check if the file type is allowed or not (txt or json)
        const allowedFileTypes = ['.txt', '.json'];
        const fileType = `.${file.name.split('.').pop()}`;
        return allowedFileTypes.includes(fileType.toLowerCase());
    }

    readAndValidateFile(file: File): Promise<FileValidation> {
        return new Promise((resolve, reject) => {
            const fileReader = new FileReader();
            fileReader.onload = (event: Event) => {
                const fileContent = fileReader.result as string;
                try {
                    this.jsonFileData = JSON.parse(fileContent);
                    if (this.isValidJsonObject(this.jsonFileData)) {
                        resolve({ isValid: true });
                    } else {
                        resolve({ isValid: false, warningMessage: this.warningMessage });
                    }
                } catch (error) {
                    console.log('Could not parse the file');
                    resolve({ isValid: false, warningMessage: this.warningMessage });
                }
            };
            fileReader.onerror = (event: Event) => {
                reject('Error reading the file.');
            };
            fileReader.readAsText(file);
        });
    }

    prepareRowData(): void {
        this.newFeedColumnData = [];
        if (this.jsonFileData.ETLData && this.jsonFileData.ETLData.length > 0) {
            const EtlData = this.jsonFileData.ETLData[0];
            for (const [key, value] of Object.entries(EtlData)) {
                this.newFeedColumnData.push({
                    name: key,
                    dataType: this.determineDataType(value)
                });
            }
        }
    }

    clearFile(uploader): void {
        this.fileValidation = {};
        this.selectedFileName = [];
        uploader.value = "";
        this.loadFromFileForm.get("file").patchValue("");
    }

    async save(): Promise<void> {
        this.loading = true;
        this.actionFlag = this.loadFromFileForm.get("uploadActions").value;
        const data = {
            FeedName: this.jsonFileData.FeedName,
            SourceSystem: this.jsonFileData.SourceSystem,
            dataItems: this.newFeedColumnData,
            action: this.actionFlag,
        };
        this.dialogRef.close(data);
        this.loading = false;
    }

    private determineDataType(value: any) {
        switch (typeof value) {
            case "string":
                return this.stringOrDateTime(value);
            case "number":
                if (Number.isInteger(value))
                    return FieldTypes.Int;
                return FieldTypes.Double;
            case "boolean":
                return FieldTypes.Bool;
            default:
                return FieldTypes.Str;
        }
    }

    private stringOrDateTime(str: string): FieldTypes.Str | FieldTypes.DateTime {
        try {
            const parseAttempt = moment(str, [moment.ISO_8601, moment.RFC_2822]);
            if (parseAttempt.isValid())
                return FieldTypes.DateTime;
            return FieldTypes.Str;
        } catch (ex) {
            return FieldTypes.Str;
        }
    }

    private isValidJsonObject(jsonObject: any): boolean {
        // Check for required object values
        // 'FeedName', 'SourceSystem', 'ETLData'
        return jsonObject && jsonObject.hasOwnProperty('FeedName')
            && jsonObject.hasOwnProperty('SourceSystem') && jsonObject.hasOwnProperty('ETLData');
    }
}
